/**
 * Custom Product Recommendations Carousel
 * Handles horizontal scrolling carousel with arrow navigation
 */

class CustomRecommendationsCarousel {
  /** @param {HTMLElement} section */
  constructor(section) {
    this.section = section;
    this.trackContainer = /** @type {HTMLElement|null} */ (section.querySelector('.custom-recommendations__track-container'));
    this.track = /** @type {HTMLElement|null} */ (section.querySelector('[data-custom-recommendations-track]'));
    if (!this.track || !this.trackContainer) return;
    this.slides = Array.from(this.track.children || []);
    this.prevButton = /** @type {HTMLButtonElement|null} */ (section.querySelector('.custom-recommendations__arrow--prev'));
    this.nextButton = /** @type {HTMLButtonElement|null} */ (section.querySelector('.custom-recommendations__arrow--next'));
    this.wrapper = /** @type {HTMLElement|null} */ (section.querySelector('.custom-recommendations__wrapper'));

    this.currentIndex = 0;
    this.slidesToScroll = 1;
    
    if (!this.track || this.slides.length === 0) return;
    
    this.init();
  }

  init() {
    this.updateButtonStates();
    this.attachEventListeners();
    this.handleResize();
    
    window.addEventListener('resize', () => this.handleResize());
  }

  attachEventListeners() {
    this.prevButton?.addEventListener('click', () => this.scrollPrev());
    this.nextButton?.addEventListener('click', () => this.scrollNext());
    
    // Touch/swipe support
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.trackContainer.addEventListener('touchstart', (/** @type {TouchEvent} */ e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    });

    this.trackContainer.addEventListener('touchmove', (/** @type {TouchEvent} */ e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    });

    this.trackContainer.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      
      const diff = startX - currentX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.scrollNext();
        } else {
          this.scrollPrev();
        }
      }
    });
  }

  handleResize() {
    this.updateButtonStates();
  }

  scrollPrev() {
    if (this.currentIndex > 0) {
      this.currentIndex -= this.slidesToScroll;
      if (this.currentIndex < 0) this.currentIndex = 0;
      this.updateCarousel();
    }
  }

  scrollNext() {
    const maxIndex = this.getMaxIndex();
    if (this.currentIndex < maxIndex) {
      this.currentIndex += this.slidesToScroll;
      if (this.currentIndex > maxIndex) this.currentIndex = maxIndex;
      this.updateCarousel();
    }
  }

  getMaxIndex() {
    const slidesPerView = this.getSlidesPerView();
    return Math.max(0, this.slides.length - slidesPerView);
  }

  getSlidesPerView() {
    if (!this.trackContainer || !this.slides.length) return 1;
    const first = /** @type {HTMLElement} */ (this.slides[0]);
    const slideWidth = first.getBoundingClientRect().width || first.offsetWidth;
    const gapValue = this.track ? getComputedStyle(this.track).gap || '0px' : '0px';
    const gap = parseFloat(gapValue) || 0;
    const containerWidth = this.trackContainer.clientWidth;
    const perView = Math.max(1, Math.floor((containerWidth + gap) / (slideWidth + gap)));
    return perView;
  }

  updateCarousel() {
    if (!this.track || !this.trackContainer || !this.slides.length) return;
    const first = /** @type {HTMLElement} */ (this.slides[0]);
    const slideWidth = first.getBoundingClientRect().width || first.offsetWidth;
    const gapValue = this.track ? getComputedStyle(this.track).gap || '0px' : '0px';
    const gap = parseFloat(gapValue) || 0;
    const offset = this.currentIndex * (slideWidth + gap);

    this.trackContainer.scrollTo({ left: offset, behavior: 'smooth' });
    this.updateButtonStates();
  }

  updateButtonStates() {
    const maxIndex = this.getMaxIndex();
    const slidesPerView = this.getSlidesPerView();
    const canScroll = this.slides.length > slidesPerView;

    if (this.wrapper) {
      this.wrapper.classList.toggle('custom-recommendations--no-scroll', !canScroll);
      this.wrapper.classList.toggle('custom-recommendations--center', !canScroll);
    }

    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
      this.prevButton.style.display = canScroll ? '' : 'none';
    }

    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex >= maxIndex;
      this.nextButton.style.display = canScroll ? '' : 'none';
    }
  }
}

// Initialize all custom recommendation carousels on the page
function initCustomRecommendationsCarousels() {
  const carouselSections = document.querySelectorAll('.custom-recommendations-section');
  carouselSections.forEach(section => {
    if (section instanceof HTMLElement) new CustomRecommendationsCarousel(section);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomRecommendationsCarousels);
} else {
  initCustomRecommendationsCarousels();
}

// Re-initialize on Shopify section updates
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  const section = target.querySelector('.custom-recommendations-section');
  if (section instanceof HTMLElement) {
    new CustomRecommendationsCarousel(section);
  }
});

// Export for use in product-recommendations web component
if (typeof window !== 'undefined') {
  /** @type {any} */ (window).CustomRecommendationsCarousel = CustomRecommendationsCarousel;
}
